import { createContext, useContext, useEffect, useState } from "react";
import { useUser } from "src/content/pages/settings/userManagement/user/userContext";
import AdminNotificationService from "src/services/AdminNotification/AdminNotificationService";
import {
  startNotificationConnection,
  getNotificationConnection
} from "src/services/signalR/signalRService";

interface NotificationItem {
  id: number;
  title: string;
  message: string;
  referenceType: string;
  referenceId: number
  createdAt: string;
}

interface NotificationContextType {
  notifications: NotificationItem[];
  unreadCount: number;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType>(
  {} as NotificationContextType
);

export const NotificationProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const { user } = useUser();

    const isAdmin = user?.role?.name === "Admin";

  const fetchUnread = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    try {
      const data = await AdminNotificationService.getUnreadNotifications();
      setNotifications(data);
    } catch (err: any) {
      if (err?.response?.status === 401) {
        console.error("Unauthorized - skipping unread fetch");
        return;
      }
      console.error("Fetch unread failed", err);
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await AdminNotificationService.markAsRead(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      console.error("Mark as read failed", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await AdminNotificationService.markAllAsRead();
      setNotifications([]);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (user?.id && user.id !== 0 && isAdmin) {
      fetchUnread();
    } else {
      setNotifications([]);

      const connection = getNotificationConnection();
      connection?.stop();
    }
  }, [user?.id, isAdmin]);

  useEffect(() => {
    if (!user?.id || user.id === 0 || !isAdmin) return;

    const initSignalR = async () => {
      await startNotificationConnection();
      const connection = getNotificationConnection();
      if (!connection) return;

      const handleNotification = (data: NotificationItem) => {
        setNotifications((prev) => {
          const exists = prev.some((n) => n.id === data.id);
          if (exists) return prev;
          return [data, ...prev];
        });
      };

      connection.off("ReceiveNotification");
      connection.on("ReceiveNotification", handleNotification);

      connection.off("ReceiveReviewNotification");
      connection.on("ReceiveReviewNotification", handleNotification);
    };

    initSignalR();

    return () => {
      const connection = getNotificationConnection();
      connection?.off("ReceiveNotification");
      connection?.off("ReceiveReviewNotification");
    };
  }, [user?.id, isAdmin]);

  return (
    <NotificationContext.Provider
      value={{
        notifications: isAdmin ? notifications : [],
        unreadCount: isAdmin ? notifications.length : 0,
        markAsRead,
        markAllAsRead
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => useContext(NotificationContext);